var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElement, Watch, PropertyConverters, P, PCSS, Components, CustomElementUtils, Utils, Debounce } from '@pacem/pacem-core';
import { Size, Colors } from '@pacem/pacem-foundation';
//namespace Pacem.Components.Fx {
const GradientChangeEventName = 'gradientchange';
class GradientChangeEvent extends Event {
    constructor() {
        super(GradientChangeEventName, { cancelable: false, bubbles: false });
    }
}
const SizeOrNumberConverter = {
    convert: (attr, el) => {
        try {
            return Size.parse(attr);
        }
        catch {
            return PropertyConverters.Number.convert(attr, el);
        }
    }
};
function pctFormatter(v) {
    return Math.round(v * 1000) / 10 + '%';
}
let PacemFxGradientStopElement = class PacemFxGradientStopElement extends Components.PacemItemElement {
    findContainer() {
        return CustomElementUtils.findAncestor(this, i => i instanceof PacemFxGradientItemElement);
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (!first && (name === 'offset' || name === 'color' || name === 'opacity' || name === 'disabled' || name === 'hide')) {
            this.dispatchEvent(new GradientChangeEvent());
        }
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemFxGradientStopElement.prototype, "offset", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemFxGradientStopElement.prototype, "color", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemFxGradientStopElement.prototype, "opacity", void 0);
PacemFxGradientStopElement = __decorate([
    CustomElement({ tagName: P + '-fx-gradient-stop' })
], PacemFxGradientStopElement);
export { PacemFxGradientStopElement };
export class PacemFxGradientItemElement extends Components.PacemCrossItemsContainerElement {
    constructor() {
        super(...arguments);
        this._dispatchGradientChangeHandler = (e) => {
            this._dispatchGradientChange();
        };
    }
    validate(item) {
        return item instanceof PacemFxGradientStopElement;
    }
    register(item) {
        const registered = super.register(item);
        if (registered) {
            item.addEventListener(GradientChangeEventName, this._dispatchGradientChangeHandler, false);
        }
        return registered;
    }
    unregister(item) {
        const unregistered = super.unregister(item);
        if (unregistered) {
            item.removeEventListener(GradientChangeEventName, this._dispatchGradientChangeHandler, false);
        }
        return unregistered;
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (!first && this.doesPropertyTriggerGradientChange(name)) {
            this._dispatchGradientChange();
        }
    }
    _dispatchGradientChange() {
        this.dispatchEvent(new GradientChangeEvent());
    }
    buildColor(color, opacity) {
        if (opacity >= 0 && opacity <= 1) {
            const clr = Colors.parse(color);
            return Colors.stringify(Utils.extend(clr, { a: opacity }));
        }
        return color;
    }
}
let PacemFxRadialGradientElement = class PacemFxRadialGradientElement extends PacemFxGradientItemElement {
    doesPropertyTriggerGradientChange(prop) {
        return ['shape', 'size', 'center', 'items', 'hide', 'disabled'].indexOf(prop) >= 0;
    }
    buildGradient() {
        const items = this.items;
        if (Utils.isNullOrEmpty(items)) {
            return '';
        }
        const shape = this.shape === 'circle' ? 'circle' : 'ellipse';
        const center = this.center ?? { x: .5, y: .5 };
        const size = this.size;
        let dim = '';
        if (!Utils.isNullOrEmpty(size)) {
            if (typeof size === 'number') {
                dim = `${size}px`;
            }
            else {
                dim = `${pctFormatter(size.width / 2)} ${pctFormatter(size.height / 2)}`;
            }
        }
        let stops = [];
        for (let { offset, color, hide, disabled, opacity } of items) {
            if (hide || disabled) {
                continue;
            }
            const clr = this.buildColor(color, opacity);
            stops.push(`${clr} ${pctFormatter(offset)}`);
        }
        return `radial-gradient(${shape} ${dim} at ${pctFormatter(center.x)} ${pctFormatter(center.y)} in oklab, ${stops.join(', ')})`;
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemFxRadialGradientElement.prototype, "shape", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Point })
], PacemFxRadialGradientElement.prototype, "center", void 0);
__decorate([
    Watch({ emit: false, converter: SizeOrNumberConverter })
], PacemFxRadialGradientElement.prototype, "size", void 0);
PacemFxRadialGradientElement = __decorate([
    CustomElement({ tagName: P + '-fx-gradient-radial' })
], PacemFxRadialGradientElement);
export { PacemFxRadialGradientElement };
let PacemFxLinearGradientElement = class PacemFxLinearGradientElement extends PacemFxGradientItemElement {
    doesPropertyTriggerGradientChange(prop) {
        return ['angle', 'items', 'hide', 'disabled'].indexOf(prop) >= 0;
    }
    buildGradient() {
        const items = this.items;
        if (Utils.isNullOrEmpty(items)) {
            return '';
        }
        const angle = this.angle ?? 0;
        let stops = [];
        for (let { offset, color, hide, disabled, opacity } of items) {
            if (hide || disabled) {
                continue;
            }
            const clr = this.buildColor(color, opacity);
            stops.push(`${clr} ${pctFormatter(offset)}`);
        }
        return `linear-gradient(${angle}deg in oklab, ${stops.join(', ')})`;
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemFxLinearGradientElement.prototype, "angle", void 0);
PacemFxLinearGradientElement = __decorate([
    CustomElement({ tagName: P + '-fx-gradient-linear' })
], PacemFxLinearGradientElement);
export { PacemFxLinearGradientElement };
let PacemFxGradientElement = class PacemFxGradientElement extends Components.PacemItemsContainerElement {
    constructor() {
        super(...arguments);
        this._gradientChangeHandler = (e) => {
            this._buildGradient();
        };
    }
    validate(item) {
        return item instanceof PacemFxGradientItemElement;
    }
    register(item) {
        const registered = super.register(item);
        if (registered) {
            item.addEventListener(GradientChangeEventName, this._gradientChangeHandler, false);
        }
        return registered;
    }
    unregister(item) {
        const unregistered = super.unregister(item);
        if (unregistered) {
            item.removeEventListener(GradientChangeEventName, this._gradientChangeHandler, false);
        }
        return unregistered;
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this._buildGradient();
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (!first) {
            switch (name) {
                case 'target':
                    if (old) {
                        this._removeGradient(old);
                    }
                case 'mode':
                case 'disabled':
                    this._buildGradient();
                    break;
            }
        }
    }
    _removeGradient(tget) {
        Utils.removeClass(tget, PCSS + '-fx-gradient-text');
        Utils.removeClass(tget, PCSS + '-fx-gradient-fill');
        tget.style.backgroundImage = '';
    }
    _buildGradient() {
        const tget = this.target;
        if (Utils.isNull(tget)) {
            return;
        }
        let gradients = '';
        const items = this.disabled ? [] : this.items ?? [];
        for (let item of items) {
            if (item.hide || item.disabled) {
                continue;
            }
            const gradient = item.buildGradient();
            if (Utils.isNullOrEmpty(gradient)) {
                continue;
            }
            gradients += gradient;
            if (!gradients.trim().endsWith(',')) {
                gradients += ',';
            }
        }
        if (!Utils.isNullOrEmpty(gradients)) {
            const t = PCSS + '-fx-gradient-text', f = PCSS + '-fx-gradient-fill';
            const ist = this.mode === 'text';
            Utils.removeClass(tget, ist ? f : t);
            Utils.addClass(tget, ist ? t : f);
            tget.style.backgroundImage = gradients.substring(0, gradients.length - 1);
        }
        else {
            this._removeGradient(tget);
        }
    }
};
__decorate([
    Watch({ converter: PropertyConverters.Element })
], PacemFxGradientElement.prototype, "target", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemFxGradientElement.prototype, "mode", void 0);
__decorate([
    Debounce(true)
], PacemFxGradientElement.prototype, "_buildGradient", null);
PacemFxGradientElement = __decorate([
    CustomElement({ tagName: P + '-fx-gradient' })
], PacemFxGradientElement);
export { PacemFxGradientElement };
